// --------------------------------------------------------------
// Created On: 2025-10-29
// Author: Zachary Thomas
//
// Last Modified: 2025-11-02
// Modified By: Zachary Thomas
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import { LOCAL_STORAGE_DEVICE_ATTRIBUTES_KEY } from "src/constants/localStorageVariables";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import deepCopy from "src/utilities/deepCopy";

const initialState: AssetGraphAttribute[] = [];

// A Redux slice for managing attribute selections that are used for graphing multiple attributes for a single asset.
const assetGraphAttributesSlice = createSlice({
  name: "assetGraphAttributes",
  initialState,
  reducers: {
    setAssetGraphAttributes: (state, action: PayloadAction<AssetGraphAttribute[]>) => {
      const payload = action.payload;
      const attributesDeepCopy = deepCopy(payload);
      // Attempt to collect previously selected attribute information from local storage.
      const checkedAttributesText = localStorage.getItem(LOCAL_STORAGE_DEVICE_ATTRIBUTES_KEY);
      if (checkedAttributesText !== null) {
        try {
          const checkedAttributes = JSON.parse(checkedAttributesText) as string[];
          // Add data about if each attribute has been checked previously.
          attributesDeepCopy.forEach((attribute) => {
            if (attribute !== undefined && checkedAttributes !== null && checkedAttributes.includes(attribute.code)) {
              attribute.checked = true;
            } else {
              attribute.checked = false;
            }
          });
        } catch (e) {
          console.error(e);
          console.error("Unable to get checked attributes from local storage.");
        }
      }
      return attributesDeepCopy;
    },
    toggleAssetGraphAttribute: (state, action: PayloadAction<string>) => {
      const payload = action.payload;
      const attributesDeepCopy = deepCopy(state);
      // Attempt to collect previously selected attribute information from local storage.
      const checkedAttributesText = localStorage.getItem(LOCAL_STORAGE_DEVICE_ATTRIBUTES_KEY);
      let checkedAttributes: string[] = [];
      if (checkedAttributesText === null) {
        checkedAttributes = [];
      } else {
        checkedAttributes = JSON.parse(checkedAttributesText) as string[];
      }
      // Check what action to perform based on the current state of the attribute.
      const targetAttribute = attributesDeepCopy.find((attribute) => attribute.code === payload);
      if (targetAttribute === undefined) {
        return attributesDeepCopy;
      } else if (targetAttribute.checked) {
        // This attribute needs to be unchecked.
        targetAttribute.checked = false;
        const index = checkedAttributes.indexOf(targetAttribute.code);
        if (index > -1) {
          checkedAttributes.splice(index, 1);
        }
      } else {
        // This attribute needs to be checked.
        targetAttribute.checked = true;
        if (!checkedAttributes.includes(payload)) {
          checkedAttributes.push(payload);
        }
      }
      localStorage.setItem(LOCAL_STORAGE_DEVICE_ATTRIBUTES_KEY, JSON.stringify(checkedAttributes));
      return attributesDeepCopy;
    },
  },
});

export const { setAssetGraphAttributes, toggleAssetGraphAttribute } = assetGraphAttributesSlice.actions;
export default assetGraphAttributesSlice.reducer;

interface AssetGraphAttribute {
  attributeId: number;
  code: string;
  name: string;
  checked: boolean;
  isAccumulative: boolean;
}
